// Wellbeing: tonight's controls in one place, and the last seven days as
// numbers. "End of day" is a button, not a time; the chair comes before
// anything else; a smoke is one tap, in the owner's own unit, recorded like
// any other variable. No streaks, no colours for good or bad, and a day with
// no record is a dash, not a miss.
import { Link, useNavigate } from '@tanstack/react-router'
import { Armchair, BedDouble, Cigarette, ClipboardCheck, Moon, Undo2 } from 'lucide-react'
import { useDays, useSettings } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import {
  beginNap,
  endDay,
  endNap,
  eveningOf,
  logSmoke,
  napCap,
  openReset,
  recentDays,
  summarise,
  unlogSmoke,
} from '@/lib/wellbeing'
import { useReset } from '@/state/reset'
import { Button, PageTitle, SectionTitle, cx } from '@/components/ui'

const dayName = (date: string) =>
  new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })

const num = (n?: number) => (n === undefined ? '·' : String(n))

export const WellbeingView = () => {
  const navigate = useNavigate()
  const days = useDays()
  const settings = useSettings()
  const now = useNow(30_000)
  const date = eveningOf(now)
  const rec = days.find((d) => d.spec.date === date)?.spec
  const today = rec ? summarise(rec) : undefined
  const running = useReset((s) => s.status === 'running')
  const open = openReset(rec)
  const nap = napCap(rec, now)
  const unit = settings.smoke_unit
  const recent = recentDays(days, 7)

  return (
    <div className="flex flex-col gap-5" data-testid="wellbeing">
      <PageTitle>Wellbeing</PageTitle>

      <section className="card p-4 flex flex-col gap-3" data-testid="wellbeing-tonight">
        <div className="flex items-baseline justify-between">
          <SectionTitle>Tonight</SectionTitle>
          <span className="text-xs text-muted">
            {today?.ended ? `Day ended ${today.ended}` : 'The day ends when you say'}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {!rec?.ended_at && (
            <Button
              big
              onClick={() => void endDay(date).then(() => navigate({ to: '/reset' }))}
              data-testid="end-of-day"
            >
              <Moon size={16} /> End of day
            </Button>
          )}
          {rec?.ended_at && !today?.resetDone && (
            <Link to="/reset" className="contents">
              <Button big variant={running || open ? 'chipOn' : undefined} data-testid="wellbeing-reset">
                <Armchair size={16} /> {running || open ? 'In the chair' : 'The chair'}
              </Button>
            </Link>
          )}
          {today?.resetDone && (
            <span className="inline-flex items-center gap-1.5 text-sm text-muted px-2 py-2">
              <Armchair size={14} /> Chair {today.resetStart}
            </span>
          )}
          {rec?.ended_at && !today?.checkedIn && (
            <Link to="/checkin" className="contents">
              <Button big data-testid="wellbeing-checkin">
                <ClipboardCheck size={16} /> Check in
              </Button>
            </Link>
          )}
          {today?.checkedIn && (
            <Link to="/checkin" className="inline-flex items-center gap-1.5 text-sm text-muted px-2 py-2">
              <ClipboardCheck size={14} /> Checked in
            </Link>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-line">
          <Button variant="chip" onClick={() => void logSmoke(date)} data-testid="smoke">
            <Cigarette size={14} /> Smoke
          </Button>
          <span className="text-sm text-muted" data-testid="smoke-line">
            {today && today.smokeTotal > 0
              ? `${today.smokeTotal} ${unit}${today.smokeTotal === 1 ? '' : 's'}, the first at ${today.firstSmoke}${
                  today.chairFirst ? ', after the chair' : ''
                }`
              : 'None yet'}
          </span>
          {today && today.smokeTotal > 0 && (
            <Button
              variant="chip"
              onClick={() => void unlogSmoke(date)}
              aria-label="Take the last one back"
              data-testid="smoke-undo"
            >
              <Undo2 size={12} />
            </Button>
          )}
          <span className="flex-1" />
          {nap ? (
            <Button variant="chipOn" onClick={() => void endNap(date)} data-testid="nap-up">
              <BedDouble size={14} /> <span className="opacity-80">Up at {nap}</span>{' '}
              <span className="ml-1.5">I'm up</span>
            </Button>
          ) : (
            <Button variant="chip" onClick={() => void beginNap(date)} data-testid="nap">
              <BedDouble size={14} /> Nap, 30 min cap
            </Button>
          )}
        </div>
      </section>

      <section>
        <SectionTitle>Last seven days</SectionTitle>
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-[13px] tabular-nums" data-testid="wellbeing-days">
            <thead className="text-[13px] font-semibold text-muted">
              <tr>
                <th className="text-left font-normal py-1.5 pr-1">Day</th>
                {/* the scales by name, never abbreviated; a phone shows them small, a desktop at size */}
                <th className="text-right font-normal py-1.5 px-1 max-md:text-[11px]">Anxiety</th>
                <th className="text-right font-normal py-1.5 px-1 max-md:text-[11px]">Unreality</th>
                <th className="text-right font-normal py-1.5 px-1 max-md:text-[11px]">Mood</th>
                <th className="text-right font-normal py-1.5 px-1">Night</th>
                <th className="text-right font-normal py-1.5 px-1">Chair</th>
                <th className="text-right font-normal py-1.5 pl-1">Smoke</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {recent.map(({ date: d, summary: s }) => (
                <tr key={d} className={cx(d === date && 'text-fg', d !== date && 'text-fg-2')}>
                  <td className="py-2 pr-1 whitespace-nowrap">{dayName(d)}</td>
                  <td className="text-right px-1">{num(s?.anxiety)}</td>
                  <td className="text-right px-1">{num(s?.unreality)}</td>
                  <td className="text-right px-1">{num(s?.mood)}</td>
                  <td className="text-right px-1 whitespace-nowrap">{s?.night ?? '·'}</td>
                  <td className="text-right px-1 whitespace-nowrap">
                    {s?.resetDone ? s.resetStart : s?.resetStart ? `(${s.resetStart})` : '·'}
                  </td>
                  <td className="text-right pl-1 whitespace-nowrap">
                    {/* how many, then the first one's time under it (set apart, not dotted together) */}
                    {s && s.smokeTotal > 0 ? (
                      <span className="inline-flex flex-col items-end leading-tight">
                        <span>{s.smokeTotal}</span>
                        <span className="text-[11px] text-faint">
                          {s.firstSmoke}
                          {s.chairFirst ? ' ✓' : ''}
                        </span>
                      </span>
                    ) : (
                      '·'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-faint mt-2">
          A dot is a day without that number. A chair time in brackets was stopped early. Under the smokes is
          the first one’s time, with a tick when the chair came first. Export and the clocks are in{' '}
          <Link to="/settings/$section" params={{ section: 'wellbeing' }} className="underline">
            Settings
          </Link>
          .
        </p>
      </section>
    </div>
  )
}
